'use client';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import MetaData from '@/utils/metaData.tsx';
import { getInitials, mapKnownCountriesToIso } from '@/utils/utils.ts';
import Spinner from 'components/v1/ui/Spinner.tsx';
import PersonAvatar from 'components/v2/appLayout/PersonAvatar.tsx';
import DetailCarousel from 'components/v2/section-detail/DetailCarousel.tsx';
import DetailTextWrapper from 'components/v2/section-detail/DetailTextWrapper.tsx';
import SectionItemBox from 'components/v2/section-detail/SectionItemBox.tsx';
import SectionTitleWrapper from 'components/v2/section-detail/SectionTitleWrapper.tsx';
import TranslateButton from 'components/v2/section-detail/TranslateButton.tsx';
import { useRepositories } from 'hooks/useRepositories.ts';
import i18n from 'i18n';

import RichText from '../section-detail/RichText.tsx';

const PharmaMedizinopferDetail = ({ id }: { id: number }) => {
  const { pharmaMedizinopferRepository } = useRepositories();
  const [isOriginalLang, setIsOriginalLang] = useState(true);
  const lang = isOriginalLang ? undefined : i18n.language;

  const { isLoading, data: item } = useQuery({
    queryKey: ['pharmaMedizinopferPost' + id + lang],
    queryFn: ({ signal }) => pharmaMedizinopferRepository.get({ id, lang, signal })
  });

  if (isLoading) return <Spinner />;
  if (!item) return;

  const flagIso = mapKnownCountriesToIso(item.country);

  return (
    <>
      <MetaData
        component="pharmaMedizinopferRepositoryDetail"
        data={item}
      />
      <div className={'grid grid-cols-1 gap-3 md:grid-cols-8'}>
        <div className={'md:col-span-1'}>
          <PersonAvatar
            link={item.avatar ? process.env.NOCO_DB_BASE_URL + '/' + item.avatar[0].path : ''}
            fallback={getInitials(item.firstname_damaged)}
            subtitle={item.age_damaged}
            size={'size-22'}
          >
            {flagIso && (
              <img
                src={`/img/country_flags/${flagIso}.png`}
                alt=""
                className={
                  'absolute right-0 bottom-0 h-6 w-auto translate-x-1/4 translate-y-1/4 rounded-sm border-1 border-white'
                }
              />
            )}
          </PersonAvatar>
        </div>

        <DetailTextWrapper
          className={'px-3 py-2 md:col-span-7'}
          title={item.firstname_damaged}
          date={item.CreatedAt}
          city={item.city}
          translateButton={
            <TranslateButton
              onClick={() => setIsOriginalLang(prev => !prev)}
              isOriginalLang={isOriginalLang}
              originalLangIsoCode={item.language}
            />
          }
        >
          <div className={'mt-2'}>
            <RichText
              text={item.remark}
              preventExpand={false}
            />
          </div>
        </DetailTextWrapper>
      </div>

      <div className={'mt-4 grid grid-cols-1 gap-3 md:grid-cols-2'}>
        <div>
          <SectionTitleWrapper
            title={'mobilfunk.field.symptom_disease'}
            dependencies={[item.symptom_disease, item.symptom_addition]}
          >
            <SectionItemBox show={item.symptom_disease}>
              {item.symptom_disease && item.symptom_disease?.replaceAll(',', ', ')}
            </SectionItemBox>
            <SectionItemBox
              className={'mt-2'}
              show={item.symptom_addition}
            >
              {item.symptom_addition}
            </SectionItemBox>
          </SectionTitleWrapper>
        </div>
        <div>
          {item.attachment && (
            <div className={'mt-5 flex justify-center'}>
              <DetailCarousel
                className={'w-2/3'}
                attachments={item.attachment}
              />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PharmaMedizinopferDetail;
